import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Bell, ArrowsClockwise, EnvelopeSimple, DeviceMobile, ChatCircle,
  AppWindow, Warning, ShieldCheck, CaretDown, CaretRight, Info, ArrowsLeftRight,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Notification Preview Tab — visualises route_alert() resolution for ALL
// canonical event types against THIS shipment, before any alert is fired.
// Read-only. Mirrors backend _resolve_route precedence ladder. Phase 49.
// ---------------------------------------------------------------------------

const EVENT_LABEL = {
  temperature_excursion:       "Temperature Excursion",
  seal_breach:                 "Seal / Light Breach",
  reefer_failure:              "Reefer Failure",
  delayed_delivery:            "Delayed Delivery",
  missing_signature:           "Missing Signature",
  device_offline:              "Device Offline",
  corrective_action_required:  "Corrective Action Required",
  corrective_action_overdue:   "Corrective Action Overdue",
  corrective_action_escalated: "Corrective Action Escalated",
  arrived_origin:              "Arrived at Origin",
  departed_origin:             "Departed Origin",
  arrived_destination:         "Arrived at Destination",
  route_deviation:             "Route Deviation",
};

const ROLE_LABEL = {
  qa_manager:         "QA Manager",
  compliance_contact: "Compliance",
  carrier:            "Carrier",
  receiver:           "Receiver",
  approved_by:        "Approver",
  monitored_by:       "Monitor",
  warehouse:          "Warehouse",
};

const STATUS_STYLE = {
  ready:               { dot: "bg-emerald-400", text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30", label: "Ready" },
  shipment_not_active: { dot: "bg-amber-400",   text: "text-amber-300",   bg: "bg-amber-500/10",   border: "border-amber-500/30",   label: "Held Until Active" },
  missing_contact:     { dot: "bg-red-400",     text: "text-red-300",     bg: "bg-red-500/10",     border: "border-red-500/30",     label: "Missing Contact" },
  no_rule_configured:  { dot: "bg-zinc-500",    text: "text-zinc-400",    bg: "bg-zinc-800/40",    border: "border-zinc-700/60",    label: "No Rule" },
};

const RESOLUTION_LABEL = {
  shipment_role:      { label: "Shipment Party",          tone: "text-emerald-300", icon: ShieldCheck },
  shipment_intrinsic: { label: "Shipment Contact",        tone: "text-teal-300",    icon: ShieldCheck },
  registry_scoped:    { label: "Scoped Registry Contact", tone: "text-sky-300",     icon: Info },
  registry_fallback:  { label: "Tenant Default Contact",  tone: "text-amber-300",   icon: Warning },
};

const CHANNEL_ICON = {
  in_app: { Icon: AppWindow,      label: "In-app" },
  email:  { Icon: EnvelopeSimple, label: "Email"  },
  sms:    { Icon: DeviceMobile,   label: "SMS"    },
  slack:  { Icon: ChatCircle,     label: "Slack"  },
};

function ChannelBadges({ channels }) {
  if (!channels || channels.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5" data-testid="np-channels">
      {channels.map((ch) => {
        const def = CHANNEL_ICON[ch] || { Icon: AppWindow, label: ch };
        const Icon = def.Icon;
        return (
          <span key={ch}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-zinc-800/70 border border-zinc-700/60 text-[10px] font-mono text-zinc-300"
            title={def.label}>
            <Icon size={10} /> {def.label}
          </span>
        );
      })}
    </div>
  );
}

function ResolutionBadge({ resolution }) {
  const def = RESOLUTION_LABEL[resolution] || {
    label: resolution || "Unknown", tone: "text-zinc-400", icon: Info,
  };
  const Icon = def.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono ${def.tone}`}>
      <Icon size={10} weight="duotone" /> {def.label}
    </span>
  );
}

function StatusPill({ status, count, onClick, active }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.no_rule_configured;
  return (
    <button onClick={onClick}
      data-testid={`np-filter-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-mono border transition-colors ${
        active ? `${s.bg} ${s.text} ${s.border}` : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800"
      }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label} <span className="font-bold">{count}</span>
    </button>
  );
}

function EventRow({ ev, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const style = STATUS_STYLE[ev.status] || STATUS_STYLE.no_rule_configured;
  const label = EVENT_LABEL[ev.event_type] || ev.event_type;
  const rule = ev.rule || {};
  const channels = rule.channels || [];
  const recipients = ev.primary?.resolved_recipients || [];
  const roles = ev.primary?.shipment_roles || [];
  const hasFallback = recipients.some((r) => r.resolution === "registry_fallback");

  return (
    <div className={`border border-zinc-800 rounded-md bg-zinc-950/40 ${style.border}`}
      data-testid={`np-event-${ev.event_type}`}>
      <button onClick={() => setOpen((v) => !v)}
        data-testid={`np-event-toggle-${ev.event_type}`}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-900/50 transition-colors text-left">
        <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-zinc-100 truncate">{label}</span>
            <Badge className={`text-[9px] font-mono ${style.bg} ${style.text} border-0`}>
              {style.label}
            </Badge>
            {hasFallback && (
              <Badge className="text-[9px] font-mono bg-amber-500/15 text-amber-300 border-0">
                <Warning size={9} className="mr-0.5" /> Fallback in use
              </Badge>
            )}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            {ev.status === "no_rule_configured" ? (
              <span>No rule defined for this event type.</span>
            ) : (
              <>
                <span>→ {roles.length > 0 ? roles.map((r) => ROLE_LABEL[r] || r).join(", ") : "no roles"}</span>
                <span className="text-zinc-700">·</span>
                <span>{recipients.length} primary {recipients.length === 1 ? "recipient" : "recipients"}</span>
                {(ev.escalation || []).length > 0 && (
                  <>
                    <span className="text-zinc-700">·</span>
                    <span>{ev.escalation.length} escalation {ev.escalation.length === 1 ? "step" : "steps"}</span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ChannelBadges channels={channels} />
          {open ? <CaretDown size={12} className="text-zinc-500" /> : <CaretRight size={12} className="text-zinc-500" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-800 px-4 py-3 space-y-3 bg-zinc-950/60">
          {ev.status === "no_rule_configured" && (
            <div className="text-[11px] text-zinc-500 italic">
              No notification rule is configured for <span className="font-mono text-zinc-300">{ev.event_type}</span> in this tenant.
              Configure one in <span className="text-teal-400">Admin Tools → Event Orchestration</span> to wire up dispatch.
            </div>
          )}

          {ev.status !== "no_rule_configured" && (
            <>
              {/* Primary */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Primary</span>
                  <span className="text-[10px] font-mono text-zinc-600">
                    {roles.map((r) => ROLE_LABEL[r] || r).join(" + ")}
                  </span>
                </div>
                {recipients.length === 0 ? (
                  <div className="text-[11px] text-red-300 italic px-2 py-1.5 rounded-sm bg-red-500/5 border border-red-500/20">
                    <Warning size={11} className="inline mr-1" />
                    No contact resolves for {roles.length === 1 ? "this role" : "these roles"}. Attach the role on Parties tab, or add a contact in the tenant registry with the matching intrinsic role.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {recipients.map((r, i) => (
                      <RecipientCard key={`p-${i}`} r={r} channels={channels} />
                    ))}
                  </div>
                )}
              </div>

              {/* Escalation */}
              {(ev.escalation || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">Escalation</div>
                  <div className="space-y-2">
                    {ev.escalation.map((step) => (
                      <div key={step.step_index} className="px-2 py-1.5 rounded-sm bg-zinc-900/60 border border-zinc-800">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-mono text-zinc-400">
                            Step {step.step_index} · after {step.after_minutes} min
                          </span>
                          <span className="text-[10px] font-mono text-zinc-500">
                            {(step.shipment_roles || []).map((r) => ROLE_LABEL[r] || r).join(" + ")}
                          </span>
                        </div>
                        {step.resolved_recipients.length === 0 ? (
                          <div className="text-[10px] italic text-amber-300/80">
                            No contact resolves for this step
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {step.resolved_recipients.map((r, i) => (
                              <RecipientCard key={`e${step.step_index}-${i}`} r={r} channels={channels} compact />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rule metadata */}
              <div className="flex items-center gap-3 flex-wrap text-[10px] font-mono text-zinc-500 pt-2 border-t border-zinc-800">
                <span>Severity ≥ <span className="text-zinc-300">{rule.severity_min || "warning"}</span></span>
                <span className="text-zinc-700">·</span>
                <span>Ack window <span className="text-zinc-300">{rule.ack_window_minutes || 30}m</span></span>
                <span className="text-zinc-700">·</span>
                <span>Ack {rule.acknowledge_required ? "required" : "optional"}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RecipientCard({ r, channels, compact = false }) {
  const isFallback = r.resolution === "registry_fallback";
  return (
    <div
      data-testid={`np-recipient-${r.role}-${r.contact_id || "missing"}`}
      className={`flex items-start gap-2 px-2 py-1.5 rounded-sm border ${
        isFallback ? "bg-amber-500/5 border-amber-500/20" : "bg-zinc-900/60 border-zinc-800"
      }`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold text-zinc-100 truncate">
            {r.contact_name || <span className="italic text-zinc-500">Unknown contact</span>}
          </span>
          <span className="text-[10px] font-mono text-zinc-500">
            · {ROLE_LABEL[r.role] || r.role}
          </span>
        </div>
        {!compact && (r.email || r.phone) && (
          <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            {r.email && <span>{r.email}</span>}
            {r.email && r.phone && <span className="text-zinc-700">·</span>}
            {r.phone && <span>{r.phone}</span>}
          </div>
        )}
        {!compact && (r.facility_name || r.organization_name) && (
          <div className="text-[10px] font-mono text-zinc-600 mt-0.5">
            {[r.facility_name, r.organization_name].filter(Boolean).join(" — ")}
          </div>
        )}
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <ResolutionBadge resolution={r.resolution} />
          {!compact && <ChannelBadges channels={channels} />}
        </div>
        {isFallback && (
          <div className="text-[10px] text-amber-300/80 mt-1 italic">
            Not assigned to this shipment. Selected from the tenant default registry.
          </div>
        )}
      </div>
    </div>
  );
}

export default function NotificationPreviewTab({ shipmentId, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(null);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/notification-rules/preview/shipment/${shipmentId}`);
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load notification preview";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    // Defer to next microtask so the effect itself does not set state
    // synchronously (keeps the react-hooks/set-state-in-effect rule happy).
    queueMicrotask(fetchPreview);
  }, [fetchPreview, reloadKey]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.events;
    return data.events.filter((e) => e.status === filter);
  }, [data, filter]);

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="np-loading">
        <div className="animate-pulse text-xs font-mono text-teal-400">Resolving notification preview…</div>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="np-error">
        <div className="text-xs text-red-300">{error}</div>
        <Button size="sm" variant="outline" className="mt-3 text-xs h-8" onClick={fetchPreview}>
          <ArrowsClockwise size={12} className="mr-1" /> Retry
        </Button>
      </Card>
    );
  }
  if (!data) return null;

  const counts = data.counts || {};
  const gate = data.dispatch_gate || {};

  return (
    <div className="space-y-4" data-testid="notification-preview-tab">
      {/* Summary card */}
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-teal-400" weight="duotone" />
              <h3 className="text-sm font-semibold text-zinc-100">Notification Preview</h3>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
              Dry-run of <span className="font-mono text-zinc-300">route_alert()</span> for every event type against
              this shipment&apos;s parties, contacts, and tenant rules.
              {" "}No notifications are sent.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchPreview} size="sm" variant="outline"
              data-testid="np-refresh"
              className="h-8 text-xs border-zinc-700 hover:bg-zinc-800">
              <ArrowsClockwise size={12} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Lifecycle gate banner */}
        <div className={`mt-3 px-3 py-2 rounded-sm border flex items-center gap-2 ${
          gate.allowed
            ? "bg-emerald-500/5 border-emerald-500/20 text-emerald-300"
            : "bg-amber-500/5 border-amber-500/20 text-amber-300"
        }`} data-testid="np-lifecycle-banner">
          {gate.allowed ? <ShieldCheck size={13} weight="duotone" /> : <Warning size={13} weight="duotone" />}
          <div className="text-[11px] font-mono flex-1">
            {gate.allowed
              ? <>Shipment is <span className="font-bold">active</span>. Real-channel dispatch (email/SMS) will fire on matching events.</>
              : <>Shipment lifecycle is <span className="font-bold">{gate.lifecycle_state || "not active"}</span>. In-app alerts will still write to the Alert Center, but email/SMS dispatch is held until the shipment is activated.</>
            }
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <button onClick={() => setFilter("all")}
            data-testid="np-filter-all"
            className={`text-[10px] font-mono px-2 py-1 rounded-sm border transition-colors ${
              filter === "all"
                ? "bg-teal-500/15 text-teal-300 border-teal-500/30"
                : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800"
            }`}>
            All <span className="font-bold">{counts.total || 0}</span>
          </button>
          <StatusPill status="ready"               count={counts.ready || 0}               active={filter === "ready"}               onClick={() => setFilter("ready")} />
          <StatusPill status="shipment_not_active" count={counts.shipment_not_active || 0} active={filter === "shipment_not_active"} onClick={() => setFilter("shipment_not_active")} />
          <StatusPill status="missing_contact"     count={counts.missing_contact || 0}     active={filter === "missing_contact"}     onClick={() => setFilter("missing_contact")} />
          <StatusPill status="no_rule_configured"  count={counts.no_rule_configured || 0}  active={filter === "no_rule_configured"}  onClick={() => setFilter("no_rule_configured")} />
        </div>
      </Card>

      {/* Event rows */}
      {filtered.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 p-6 text-center" data-testid="np-empty">
          <div className="text-xs text-zinc-500">No events match this filter.</div>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((ev) => (
            <EventRow key={ev.event_type}
              ev={ev}
              defaultOpen={["missing_contact", "shipment_not_active"].includes(ev.status)}
            />
          ))}
        </div>
      )}

      {/* Footnote — legend */}
      <Card className="bg-zinc-950/40 border-zinc-800 p-3">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
          <ArrowsLeftRight size={10} /> Resolution precedence
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
          {[
            { k: "shipment_role",      desc: "Exact party role attached to this shipment." },
            { k: "shipment_intrinsic", desc: "Contact attached to this shipment whose registry role maps to the requested role." },
            { k: "registry_scoped",    desc: "Tenant registry contact, scoped to orgs/facilities this shipment touches." },
            { k: "registry_fallback",  desc: "Tenant-wide registry fallback. Not from this shipment — review before activation." },
          ].map((r) => (
            <div key={r.k} className="flex items-start gap-2">
              <div className="shrink-0 pt-0.5"><ResolutionBadge resolution={r.k} /></div>
              <span className="text-zinc-500">— {r.desc}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
